import { Link } from 'react-router'
import { Minus, Plus, ShoppingBag, Trash2, X } from 'lucide-react'
import { useCart } from '../context/CartContext'
import { useCatalog } from '../context/CatalogContext'
import { useLanguage } from '../context/LanguageContext'
import { formatPrice } from '../data/products'
import { ProductThumb } from './ProductMedia'

export default function CartDrawer() {
  const { items, itemCount, subtotal, isOpen, closeCart, setQuantity, removeItem } = useCart()
  const { t, language } = useLanguage()
  const { shopEnabled } = useCatalog()

  if (!shopEnabled) return null

  return (
    <>
      <div
        className={`fixed inset-0 z-[70] bg-ng-dark/40 backdrop-blur-[2px] transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={closeCart}
        aria-hidden="true"
      />

      <aside
        className={`fixed inset-y-0 right-0 z-[75] flex w-full max-w-md flex-col bg-ng-beige shadow-2xl transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={t('cart.title')}
      >
        <div className="flex items-center justify-between border-b border-ng-dark/10 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-ng-dark text-white">
              <ShoppingBag className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <h2 className="font-display text-xl font-semibold text-ng-dark">{t('cart.title')}</h2>
              <p className="text-xs text-ng-dark/55">
                {itemCount} {itemCount > 1 ? t('cart.items') : t('cart.item')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeCart}
            className="rounded-lg p-2 text-ng-dark/60 transition-colors hover:bg-ng-dark/5 hover:text-ng-dark"
            aria-label={t('common.closeMenu')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center px-4 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-ng-dark/5">
                <ShoppingBag className="h-7 w-7 text-ng-mid" aria-hidden="true" />
              </div>
              <p className="font-display text-xl font-semibold text-ng-dark">{t('checkoutPage.emptyCart')}</p>
              <p className="mt-2 text-sm text-ng-dark/60">{t('cart.emptyHint')}</p>
              <Link
                to="/boutique"
                onClick={closeCart}
                className="mt-6 inline-flex items-center justify-center rounded-full bg-ng-dark px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-ng-mid"
              >
                {t('checkoutPage.viewShop')}
              </Link>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="flex gap-3 rounded-2xl border border-ng-dark/8 bg-white/90 p-3 shadow-sm"
                >
                  <div className="h-28 w-28 shrink-0 overflow-hidden rounded-2xl border border-ng-dark/8 bg-white">
                    <ProductThumb
                      product={item}
                      alt={t(`shopProducts.${item.id}.name`)}
                      compact
                      className="!aspect-auto h-full w-full"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="truncate font-semibold text-ng-dark">
                          {t(`shopProducts.${item.id}.name`)}
                        </p>
                        <p className="text-sm text-ng-brown">{formatPrice(item.price, language)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="rounded-lg p-1.5 text-ng-dark/40 transition-colors hover:bg-red-50 hover:text-red-600"
                        aria-label={t('cart.remove')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="inline-flex items-center rounded-full border border-ng-dark/10 bg-ng-beige">
                        <button
                          type="button"
                          onClick={() => setQuantity(item.id, item.quantity - 1)}
                          className="p-1.5 text-ng-dark transition-colors hover:text-ng-mid"
                          aria-label="-"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="min-w-[1.75rem] text-center text-sm font-semibold text-ng-dark">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => setQuantity(item.id, item.quantity + 1)}
                          className="p-1.5 text-ng-dark transition-colors hover:text-ng-mid"
                          aria-label="+"
                          disabled={item.quantity >= item.stock}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <span className="text-sm font-semibold text-ng-dark">
                        {formatPrice(item.lineTotal, language)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-ng-dark/10 bg-white/80 px-5 py-5 backdrop-blur">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-sm text-ng-dark/70">{t('cart.subtotal')}</span>
              <span className="font-display text-xl font-bold text-ng-dark">
                {formatPrice(subtotal, language)}
              </span>
            </div>
            <p className="mb-1 text-xs text-ng-dark/50">{t('cart.shippingNote')}</p>
            <p className="mb-4 text-xs text-ng-dark/55">{t('cart.stripeNote')}</p>
            <Link
              to="/commande"
              onClick={closeCart}
              className="flex w-full items-center justify-center rounded-full bg-ng-dark px-5 py-3 text-sm font-semibold text-white transition hover:bg-ng-mid"
            >
              {t('cart.checkout')}
            </Link>
          </div>
        )}
      </aside>
    </>
  )
}
